import { scrubString, scrubUnknownToString } from './logScrubber';

export type MsalErrorDetails = {
  name?: string;
  message?: string;
  errorCode?: string;
  subError?: string;
  correlationId?: string;
  traceId?: string;
  timestamp?: string;
  status?: number;
  claims?: string;
};

type MsalLikeError = {
  name?: unknown;
  message?: unknown;
  errorCode?: unknown;
  subError?: unknown;
  correlationId?: unknown;
  traceId?: unknown;
  timestamp?: unknown;
  status?: unknown;
  claims?: unknown;
};

const toOptionalScrubbedString = (value: unknown): string | undefined => {
  if (typeof value === 'string') return scrubString(value);
  if (value == null) return undefined;
  return scrubString(scrubUnknownToString(value, 2048));
};

const toOptionalNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
};

/**
 * Best-effort extraction of useful MSAL/browser auth error fields without leaking secrets.
 * Safe to pass any thrown value (Error, string, object, axios error, etc.).
 */
export function getMsalErrorDetails(error: unknown): MsalErrorDetails {
  if (typeof error === 'string') {
    return { message: scrubString(error) };
  }

  if (!error || typeof error !== 'object') {
    return { message: toOptionalScrubbedString(error) };
  }

  const e = error as MsalLikeError;

  const out: MsalErrorDetails = {
    name: toOptionalScrubbedString(e.name),
    message: toOptionalScrubbedString(e.message),
    errorCode: toOptionalScrubbedString(e.errorCode),
    subError: toOptionalScrubbedString(e.subError),
    correlationId: toOptionalScrubbedString(e.correlationId),
    traceId: toOptionalScrubbedString(e.traceId),
    timestamp: toOptionalScrubbedString(e.timestamp),
    status: toOptionalNumber(e.status),
    claims: toOptionalScrubbedString(e.claims),
  };

  // Trim undefined keys to keep logs tight.
  Object.keys(out).forEach((k) => {
    const key = k as keyof MsalErrorDetails;
    if (out[key] === undefined) delete out[key];
  });

  return out;
}


